import { useMutation, useQuery } from "@tanstack/react-query";
import type { MailClientSettings } from "@websede/contracts";
import { Download, KeyRound, Smartphone, TabletSmartphone } from "lucide-react";
import { type FormEvent, type ReactNode, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { ApiRequestError, api } from "../../api/client.js";
import { CopyValue } from "../../components/copy-value.js";
import { Button, Card, ErrorNotice, Field, Input } from "../../components/form.js";

type Platform = "ios" | "android" | "other";

function platform(): Platform {
  const agent = typeof navigator === "undefined" ? "" : navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(agent)) return "ios";
  if (/Android/.test(agent)) return "android";
  return "other";
}

/**
 * Everything a phone or desktop app needs: a fresh app password, a one-tap profile for Apple
 * devices, the Android steps and the server settings to type in by hand.
 */
export function DevicesPage() {
  const { t } = useTranslation();
  const settings = useQuery({
    queryKey: ["mail", "devices"],
    queryFn: () => api.mail.devices(),
    retry: false,
  });
  const noMailbox = settings.error instanceof ApiRequestError && settings.error.status === 404;
  const s = settings.data;
  const cards: Record<Platform, ReactNode> = {
    ios: s ? <AppleCard key="ios" /> : null,
    android: s ? <AndroidCard key="android" settings={s} /> : null,
    other: null,
  };
  const order: Platform[] = platform() === "android" ? ["android", "ios"] : ["ios", "android"];
  return (
    <section className="mx-auto flex w-full max-w-2xl flex-col gap-6 p-4 md:p-8">
      <div>
        <h1 className="text-[22px] text-ink">{t("nav.devices")}</h1>
        <p className="mt-1 text-sm text-ink-3">{t("devices.intro")}</p>
      </div>
      {noMailbox ? (
        <p className="rounded-2xl border border-rule bg-surface p-5 text-sm text-ink-2">
          {t("mail.noMailbox")} {t("mail.askAdmin")}
        </p>
      ) : (
        <ErrorNotice error={settings.error} />
      )}
      {s ? (
        <>
          <AppPasswordCard />
          {order.map((p) => cards[p])}
          <ManualCard settings={s} />
        </>
      ) : null}
    </section>
  );
}

function AppPasswordCard() {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState("");
  const issue = useMutation({
    mutationFn: () => api.mail.appPassword(currentPassword),
    onSuccess: () => setCurrentPassword(""),
  });
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    issue.mutate();
  };
  return (
    <Card title={t("devices.password.title")}>
      <p className="text-sm text-ink-2">{t("devices.password.why")}</p>
      {issue.data ? (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 rounded-xl bg-accent-soft px-4 py-3">
            <KeyRound className="size-5 shrink-0 text-accent" />
            <code className="min-w-0 flex-1 font-mono text-base break-all text-ink select-all">
              {issue.data.appPassword}
            </code>
            <CopyValue value={issue.data.appPassword} />
          </div>
          <p className="text-xs text-ink-3">{t("devices.password.shownOnce")}</p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-3">
          <Field label={t("devices.password.confirm")} hint={t("devices.password.replaces")}>
            <Input
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          </Field>
          <ErrorNotice error={issue.error} />
          <div>
            <Button type="submit" disabled={issue.isPending || !currentPassword}>
              <KeyRound className="size-4" />
              {t("devices.password.create")}
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}

function Steps({ children }: { children: ReactNode }) {
  return (
    <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm text-ink-2 marker:text-ink-3">
      {children}
    </ol>
  );
}

function AppleCard() {
  const { t } = useTranslation();
  return (
    <Card title={t("devices.apple.title")}>
      <p className="flex items-start gap-2 text-sm text-ink-2">
        <TabletSmartphone className="mt-0.5 size-4 shrink-0 text-ink-3" />
        {t("devices.apple.what")}
      </p>
      <Steps>
        <li>{t("devices.apple.step1")}</li>
        <li>{t("devices.apple.step2")}</li>
        <li>{t("devices.apple.step3")}</li>
      </Steps>
      <div>
        <a
          href={api.mail.appleProfileUrl()}
          download="websede.mobileconfig"
          className="inline-flex h-10 items-center gap-2 rounded-full bg-accent px-5 text-sm font-medium text-white hover:shadow-1"
        >
          <Download className="size-4" />
          {t("devices.apple.download")}
        </a>
      </div>
      <p className="text-xs text-ink-3">{t("devices.apple.unverified")}</p>
    </Card>
  );
}

/** The part after the @: what Gmail pre-fills as the server name, which the user replaces. */
export function domainOf(address: string): string {
  return address.slice(address.lastIndexOf("@") + 1);
}

function AndroidCard({ settings }: { settings: MailClientSettings }) {
  const { t } = useTranslation();
  const smtp = settings.smtp[0];
  const domain = domainOf(settings.address);
  const strong = <strong className="font-medium text-ink" />;
  return (
    <Card title={t("devices.android.title")}>
      <p className="flex items-start gap-2 text-sm font-medium text-ink">
        <Smartphone className="mt-0.5 size-4 shrink-0 text-ink-3" />
        {t("devices.android.mail")}
      </p>
      <Steps>
        <li>
          <Trans i18nKey="devices.android.mail1" components={{ b: strong }} />
        </li>
        <li>
          <Trans
            i18nKey="devices.android.mail2"
            values={{ address: settings.address }}
            components={{ b: strong }}
          />
        </li>
        <li>
          <Trans
            i18nKey="devices.android.mail3"
            values={{ domain, host: settings.imap.host }}
            components={{ b: strong }}
          />
        </li>
        <li>
          <Trans
            i18nKey="devices.android.mail4"
            values={{ domain, host: smtp?.host ?? settings.imap.host }}
            components={{ b: strong }}
          />
        </li>
      </Steps>
      <p className="text-xs text-ink-3">{t("devices.android.outbox")}</p>
      <p className="flex items-start gap-2 text-sm font-medium text-ink">
        <Smartphone className="mt-0.5 size-4 shrink-0 text-ink-3" />
        {t("devices.android.dav")}
      </p>
      <Steps>
        <li>
          <Trans i18nKey="devices.android.dav1" components={{ b: strong }} />
        </li>
        <li>
          <Trans
            i18nKey="devices.android.dav2"
            values={{ url: settings.davUrl, address: settings.address }}
            components={{ b: strong }}
          />
        </li>
        <li>{t("devices.android.dav3")}</li>
      </Steps>
    </Card>
  );
}

function ManualCard({ settings }: { settings: MailClientSettings }) {
  const { t } = useTranslation();
  const security = (value: "tls" | "starttls") => (value === "tls" ? "SSL/TLS" : "STARTTLS");
  const rows: Array<[string, string, string?]> = [
    [t("devices.manual.username"), settings.username],
    [t("devices.manual.password"), t("devices.manual.passwordValue")],
    [
      t("devices.manual.imap"),
      settings.imap.host,
      `${settings.imap.port} · ${security(settings.imap.security)}`,
    ],
    ...settings.smtp.map((server, i): [string, string, string] => [
      i === 0 ? t("devices.manual.smtp") : t("devices.manual.smtpAlt"),
      server.host,
      `${server.port} · ${security(server.security)}`,
    ]),
    [t("devices.manual.dav"), settings.davUrl],
  ];
  return (
    <Card title={t("devices.manual.title")}>
      <p className="text-sm text-ink-2">{t("devices.manual.auto", { host: settings.imap.host })}</p>
      <dl className="flex flex-col divide-y divide-rule text-sm">
        {rows.map(([label, value, detail]) => (
          <div key={label} className="flex items-center gap-3 py-2">
            <dt className="w-24 shrink-0 text-ink-3 md:w-40">{label}</dt>
            <dd className="min-w-0 flex-1">
              <span className="block break-all text-ink">{value}</span>
              {detail ? <span className="block text-xs text-ink-3">{detail}</span> : null}
            </dd>
            {label === t("devices.manual.password") ? null : <CopyValue value={value} />}
          </div>
        ))}
      </dl>
    </Card>
  );
}
